'use client';

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, Check, Loader2 } from 'lucide-react';
import { buttonVariants } from '../ui/button';
import { useCartAnimationStore } from '@/src/lib/state-stores/useCartAnimationStore';
import clsx from 'clsx';

import { useUnifiedCartStore } from '@/src/lib/state-stores/unifiedCartStore';

export interface AddToCartButtonProps {
  onClick: (e?: React.MouseEvent<HTMLButtonElement>) => Promise<void>;
  idleText?: string;
  successText?: string;
  className?: string;
  disabled?: boolean;
  imageSrc?: string;
  targetBadgeId?: string;
}

export const AddToCartButton: React.FC<AddToCartButtonProps> = ({
  onClick,
  idleText = 'Add to Cart',
  successText = 'Added!',
  className = '',
  disabled = false,
  imageSrc,
  targetBadgeId = 'cart-badge',
}) => {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success'>('idle');
  const { spawnFlyingItem } = useCartAnimationStore();

  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    if (status !== 'idle' || disabled) return;

    // Capture dynamic click coordinates directly from click event or button center
    const buttonRect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX && e.clientX > 0 ? e.clientX : buttonRect.left + buttonRect.width / 2;
    const clickY = e.clientY && e.clientY > 0 ? e.clientY : buttonRect.top + buttonRect.height / 2;

    // Current baseline count for smooth transition upon particle impact
    const unifiedStore = useUnifiedCartStore.getState();
    const currentCount =
      unifiedStore.cartsCount > 0
        ? unifiedStore.cartsCount
        : unifiedStore.data?.stores?.length || 0;

    // 1. Enter loading state immediately upon user click
    setStatus('loading');

    try {
      // 2. Perform the add-to-cart request
      await onClick(e);

      // 3. On successful response: launch the flying animation and switch to success state
      const unifiedStore = useUnifiedCartStore.getState();
      const currentCount =
        unifiedStore.cartsCount > 0
          ? unifiedStore.cartsCount
          : unifiedStore.data?.stores?.length || 0;

      spawnFlyingItem(clickX, clickY, imageSrc, targetBadgeId, undefined, currentCount);

      setStatus('success');
      setTimeout(() => {
        setStatus('idle');
      }, 2000);
    } catch (error) {
      console.error('Failed to add to cart:', error);
      setStatus('idle');
    }
  };

  return (
    <motion.button
      whileTap={status === 'idle' && !disabled ? { scale: 0.95 } : undefined}
      onClick={handleClick}
      disabled={status === 'loading' || disabled}
      className={clsx(
        buttonVariants({ variant: 'default', size: 'default' }),
        status === 'loading' && 'pointer-events-none',
        className,
      )}
      aria-live="polite"
      aria-busy={status === 'loading'}
    >
      <AnimatePresence mode="wait">
        {status === 'idle' && (
          <motion.div
            key="idle"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="flex items-center gap-2"
          >
            <ShoppingCart className="h-5 w-5" />
            <span>{idleText}</span>
          </motion.div>
        )}

        {status === 'loading' && (
          <motion.div
            key="loading"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
            className="flex items-center justify-center"
          >
            <Loader2 className="h-5 w-5 animate-spin" />
          </motion.div>
        )}

        {status === 'success' && (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: [0.8, 1.15, 1] }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.4 }}
            className="flex items-center gap-2 text-green-100"
          >
            <Check className="h-5 w-5" />
            <span>{successText}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.button>
  );
};

export default AddToCartButton;
